import localforage from 'localforage';
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';

// --- Store cấp module để đồng bộ hóa ---

// Cache trong bộ nhớ cho các giá trị của localforage
const cache = new Map<string, unknown>();
// Các subscribers cho mỗi key
const subscribers = new Map<string, Set<() => void>>();
// Các promise cho các lệnh gọi localforage.getItem đang diễn ra
const promises = new Map<string, Promise<void>>();

/**
 * Đăng ký một callback để theo dõi các thay đổi cho một key cụ thể.
 * Xử lý việc tìm nạp dữ liệu ban đầu từ localforage.
 * @param key - Key trong localforage.
 * @param callback - Callback sẽ chạy khi có thay đổi.
 * @param initialValue - Giá trị ban đầu được sử dụng trước khi dữ liệu được tải.
 * @returns Một hàm để hủy đăng ký.
 */
function subscribe<T>(key: string, callback: () => void, initialValue: T): () => void {
	let keySubscribers = subscribers.get(key);
	if (!keySubscribers) {
		keySubscribers = new Set();
		subscribers.set(key, keySubscribers);
	}
	keySubscribers.add(callback);

	// Tìm nạp từ localforage nếu không có trong cache và chưa được tìm nạp.
	if (!cache.has(key) && !promises.has(key)) {
		const promise = localforage
			.getItem<T>(key)
			.then((value) => {
				cache.set(key, value ?? initialValue);
			})
			.catch((error) => {
				console.error(`useLocalForage: Không thể tìm nạp '${key}' từ storage.`, error);
				// Khi có lỗi, vẫn đặt giá trị ban đầu để tránh bị kẹt ở trạng thái đang tải.
				cache.set(key, initialValue);
			})
			.finally(() => {
				promises.delete(key);
				// Thông báo cho tất cả các subscribers của key này rằng dữ liệu đã sẵn sàng.
				subscribers.get(key)?.forEach((cb) => cb());
			});
		promises.set(key, promise);
	}

	return () => {
		const currentSubscribers = subscribers.get(key);
		if (currentSubscribers) {
			currentSubscribers.delete(callback);
			if (currentSubscribers.size === 0) {
				subscribers.delete(key);
			}
		}
	};
}

/**
 * Lấy một snapshot của giá trị hiện tại cho một key từ cache trong bộ nhớ.
 * @param key - Key.
 * @param initialValue - Giá trị ban đầu dự phòng.
 * @returns Giá trị trong cache hoặc giá trị ban đầu.
 */
function getSnapshot<T>(key: string, initialValue: T): T {
	// console.log('localforage:getSnapshot', key, cache.get(key), initialValue);
	return (cache.get(key) as T) ?? initialValue;
}

/**
 * Cập nhật một giá trị trong localforage và cache trong bộ nhớ, sau đó thông báo cho các subscribers.
 * @param key - Key.
 * @param value - Giá trị mới.
 */
async function setItem<T>(key: string, value: T) {
	// console.log('localforage:setItem', key, value);
	cache.set(key, value);
	// Thông báo cho các subscribers ngay lập tức để có giao diện người dùng phản hồi nhanh (cập nhật lạc quan).
	subscribers.get(key)?.forEach((cb) => cb());
	await localforage.setItem(key, value);
}

/**
 * Xóa một giá trị khỏi localforage và cache trong bộ nhớ, sau đó thông báo cho các subscribers.
 * @param key - Key.
 * @param initialValue - Giá trị để đặt lại.
 */
async function removeItem<T>(key: string, initialValue: T) {
	cache.set(key, initialValue);
	// Thông báo cho các subscribers ngay lập tức.
	subscribers.get(key)?.forEach((cb) => cb());
	await localforage.removeItem(key);
}

// --- Hook ---

/**
 * Một hook React để quản lý dữ liệu trong localforage, được đồng bộ hóa trên tất cả các instance của hook
 * sử dụng cùng một key thông qua `useSyncExternalStore`.
 *
 * @param key Key cho mục trong localforage.
 * @param initialValue Giá trị ban đầu để sử dụng nếu mục không tồn tại trong localforage.
 * @returns Một tuple: [storedValue, setValue, removeItem, loading, error]
 */
function useLocalforage<T>(
	key: string,
	initialValue: T,
): [T, (value: T | ((val: T) => T)) => Promise<void>, () => Promise<void>, boolean, Error | null] {
	const storedValue = useSyncExternalStore(
		(onStoreChange) => subscribe(key, onStoreChange, initialValue),
		() => getSnapshot(key, initialValue),
		() => initialValue, // Dành cho server-side rendering
	);

	// Trạng thái loading là true nếu có một promise tìm nạp tồn tại cho key này.
	const [loading, setLoading] = useState(() => promises.has(key));
	const [error, setError] = useState<Error | null>(null);

	useEffect(() => {
		// Effect này theo dõi trạng thái loading và error của lần tìm nạp ban đầu.
		const promise = promises.get(key);
		if (promise) {
			setLoading(true);
			promise
				.catch((err) => setError(err))
				.finally(() => {
					setLoading(false);
				});
		} else {
			setLoading(false);
		}
	}, [key]);

	const setValue = useCallback(
		async (value: T | ((val: T) => T)) => {
			try {
				const currentValue = getSnapshot(key, initialValue);
				const valueToStore = value instanceof Function ? value(currentValue) : value;
				await setItem(key, valueToStore);
			} catch (err) {
				setError(err as Error);
			}
		},
		[key, initialValue],
	);

	const removeItemCallback = useCallback(async () => {
		try {
			await removeItem(key, initialValue);
		} catch (err) {
			setError(err as Error);
		}
	}, [key, initialValue]);

	return [storedValue, setValue, removeItemCallback, loading, error];
}

export default useLocalforage;
